import { z } from "zod";

/** Room limits per retailer tier. */
export const TIER_LIMITS = { 1: 1, 2: 4, 3: 10 } as const;
export type Tier = keyof typeof TIER_LIMITS;
export const TIERS: readonly Tier[] = [1, 2, 3];
export const STORAGE_KEY = "red.retailer.floor.v1";

export const LIMITS = { name: 60, description: 1000, assistantName: 60, assistantRole: 600, explore: 120, exploreItems: 20, retailer: 80 } as const;

const text = (max: number) => z.string().max(max);

export const roomSchema = z.object({
  id: z.string().min(1).max(40),
  name: text(LIMITS.name),
  description: text(LIMITS.description),
  assistant: z.object({ name: text(LIMITS.assistantName), role: text(LIMITS.assistantRole) }),
  explore: z.array(text(LIMITS.explore)).max(LIMITS.exploreItems)
});
export type Room = z.infer<typeof roomSchema>;

export const floorPlanSchema = z.object({
  version: z.literal(1),
  retailer: text(LIMITS.retailer),
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  rooms: z.array(roomSchema).max(TIER_LIMITS[3])
}).refine((p) => p.rooms.length <= TIER_LIMITS[p.tier], { message: "More rooms than the tier allows", path: ["rooms"] });
export type FloorPlan = z.infer<typeof floorPlanSchema>;

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

let seq = 0;
export function newRoomId(): string {
  return `room-${Date.now().toString(36)}-${(seq++).toString(36)}`;
}

export function emptyRoom(id = newRoomId()): Room {
  return { id, name: "", description: "", assistant: { name: "", role: "" }, explore: [] };
}

export function emptyPlan(): FloorPlan {
  return { version: 1, retailer: "", tier: 1, rooms: [emptyRoom()] };
}

export const roomLimit = (tier: Tier): number => TIER_LIMITS[tier];
export const canAddRoom = (p: FloorPlan): boolean => p.rooms.length < roomLimit(p.tier);

export function addRoom(p: FloorPlan, room: Room = emptyRoom()): FloorPlan {
  if (!canAddRoom(p)) throw new Error(`Tier ${p.tier} allows ${roomLimit(p.tier)} room${roomLimit(p.tier) === 1 ? "" : "s"}`);
  return { ...p, rooms: [...p.rooms, room] };
}

export function removeRoom(p: FloorPlan, id: string): FloorPlan {
  return { ...p, rooms: p.rooms.filter((r) => r.id !== id) };
}

export function updateRoom(p: FloorPlan, id: string, patch: (r: Room) => Room): FloorPlan {
  return { ...p, rooms: p.rooms.map((r) => (r.id === id ? patch(r) : r)) };
}

/** Can the plan move to `tier`? Downgrading below the current room count is refused (rooms are never dropped silently). */
export function canSetTier(p: FloorPlan, tier: Tier): boolean {
  return p.rooms.length <= roomLimit(tier);
}

export function setTier(p: FloorPlan, tier: Tier): FloorPlan {
  if (!canSetTier(p, tier)) throw new Error(`Tier ${tier} allows ${roomLimit(tier)} room${roomLimit(tier) === 1 ? "" : "s"}; remove rooms first`);
  return { ...p, tier };
}

export function addExplore(r: Room, item: string): Room {
  const t = item.trim();
  if (!t || r.explore.length >= LIMITS.exploreItems) return r;
  return { ...r, explore: [...r.explore, t.slice(0, LIMITS.explore)] };
}

export function removeExplore(r: Room, index: number): Room {
  return { ...r, explore: r.explore.filter((_, i) => i !== index) };
}

export interface Issue {
  /** Room id, or "plan". */
  where: string;
  field: string;
  message: string;
}

/** What still needs filling in before the plan is ready to submit. */
export function validate(p: FloorPlan): Issue[] {
  const out: Issue[] = [];
  if (!p.retailer.trim()) out.push({ where: "plan", field: "retailer", message: "Enter the retailer name." });
  if (p.rooms.length === 0) out.push({ where: "plan", field: "rooms", message: "Add at least one room." });
  if (p.rooms.length > roomLimit(p.tier)) out.push({ where: "plan", field: "rooms", message: `Tier ${p.tier} allows ${roomLimit(p.tier)} rooms.` });
  const names = new Map<string, number>();
  p.rooms.forEach((r, i) => {
    const n = `Room ${i + 1}`;
    if (!r.name.trim()) out.push({ where: r.id, field: "name", message: `${n}: enter a room name.` });
    else {
      const k = r.name.trim().toLowerCase();
      if (names.has(k)) out.push({ where: r.id, field: "name", message: `${n}: another room is already called "${r.name.trim()}".` });
      names.set(k, i);
    }
    if (r.description.trim().length < 20) out.push({ where: r.id, field: "description", message: `${n}: describe what the room looks like (at least 20 characters).` });
    if (!r.assistant.name.trim()) out.push({ where: r.id, field: "assistantName", message: `${n}: name the assistant.` });
    if (!r.assistant.role.trim()) out.push({ where: r.id, field: "assistantRole", message: `${n}: describe the assistant's role.` });
    if (r.explore.length === 0) out.push({ where: r.id, field: "explore", message: `${n}: add at least one thing to explore.` });
  });
  return out;
}

/** Parse a stored or imported plan; returns null (never throws) when it is not a valid plan. */
export function parsePlan(json: string): FloorPlan | null {
  try {
    const r = floorPlanSchema.safeParse(JSON.parse(json));
    return r.success ? r.data : null;
  } catch {
    return null;
  }
}

export function exportPlan(p: FloorPlan): string {
  return JSON.stringify(p, null, 2);
}

export function loadPlan(store?: KeyValueStore): FloorPlan {
  const raw = store?.getItem(STORAGE_KEY);
  return (raw && parsePlan(raw)) || emptyPlan();
}

export function savePlan(p: FloorPlan, store?: KeyValueStore): void {
  try {
    store?.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* storage full or blocked: the plan still works for this visit */
  }
}

export function clearPlan(store?: KeyValueStore): void {
  try {
    if (store?.removeItem) store.removeItem(STORAGE_KEY);
    else store?.setItem(STORAGE_KEY, "");
  } catch {
    /* ignore */
  }
}
